import React from "react";

import { CircularProgress } from "@mui/material";
import { IconButton, Tooltip } from "@mui/material";
import { Add as IconAdd } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { FiPrinter, FiTrash2 } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { ReactSVG } from "react-svg";

import Button, { Color } from "../../../../../../components/Form/Button";
import { PRIVATE_ROUTES } from "../../../../../../constants";
import { Waybill } from "../../../../../../models/waybill";
import { ShipmentStatus } from "../../../../../../util/enums";
import useStyles, { GenerateCSVButton } from "./styled";
import { TextButton } from "Components/Generic";

const ICON_BUTTON_COLOR = "#475467";
const ACTION_COMMUNICATION_ICON = require("../../../../../../assets/icons/action-communication-icon.svg");
const ACTION_EDIT_ICON = require("../../../../../../assets/icons/action-edit-icon.svg");
const ACTION_VIEW_ICON = require("../../../../../../assets/icons/action-view-icon.svg");
const SETTING_PREFERENCE = require("../../../../../../assets/icons/setting-preference.svg");

export default function Buttons() {
  const history = useHistory();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const isWaybillUnavailable = (waybill: Waybill): boolean => {
    const { statusShipment } = waybill;
    const shipmentStatus = waybill.shipmentTracking?.shipmentStatus;

    const isShipmentCompletedOrDeleted =
      statusShipment === ShipmentStatus.DELETED || shipmentStatus === ShipmentStatus.COMPLETED;

    return isShipmentCompletedOrDeleted;
  };

  const setEditButton = (waybill: Waybill) => {
    const { id } = waybill;

    if (isWaybillUnavailable(waybill)) return null;

    return (
      <Tooltip data-cy="waybill-action-edit" title={translate("edit") ?? ""} placement="top">
        <div onClick={() => history.push(`${PRIVATE_ROUTES.SHIPMENTS_MNT}/${Number(id)}`)}>
          <ReactSVG
            src={ACTION_EDIT_ICON}
            className={classes.iconButton}
            color={ICON_BUTTON_COLOR}
          />
        </div>
      </Tooltip>
    );
  };

  const setViewButton = (waybill: Waybill, callback: () => void) => {
    const { statusShipment } = waybill;

    if (statusShipment === ShipmentStatus.DELETED) return null;

    return (
      <Tooltip data-cy="waybill-action-view" title={translate("view") ?? ""} placement="top">
        <div onClick={() => callback()}>
          <ReactSVG
            src={ACTION_VIEW_ICON}
            className={classes.iconButton}
            color={ICON_BUTTON_COLOR}
          />
        </div>
      </Tooltip>
    );
  };

  const setDeleteButton = (
    waybill: Waybill,
    onDelete: () => void,
    isShowingCircularIcon?: boolean
  ) => {
    if (isWaybillUnavailable(waybill)) return null;

    if (isShowingCircularIcon) return <CircularProgress size={18} />;

    return (
      <Tooltip data-cy="waybill-action-delete" title={translate("delete") ?? ""} placement="top">
        <div>
          <FiTrash2
            className={classes.iconButton}
            color="#f97066"
            size={18}
            onClick={() => onDelete()}
          />
        </div>
      </Tooltip>
    );
  };

  const setSendEmailButton = (
    waybill: Waybill,
    callback: (emails: string[], phones: string[]) => void
  ) => {
    const { jobs } = waybill;
    const customerEmails: string[] = [];
    const customerPhones: string[] = [];

    if (!jobs?.length) return null;

    jobs?.forEach((job) => {
      if (job.customer) {
        const { email, phone } = job.customer;

        if (email && email.trim() !== "") customerEmails.push(email);
        if (phone && phone.trim() !== "") customerPhones.push(phone);
      }
    });

    const hasCustomer = !!customerEmails.length && !!customerPhones.length;

    if (!hasCustomer || isWaybillUnavailable(waybill)) return null;

    return (
      <Tooltip
        data-cy="waybill-send-email"
        title={translate("communication") ?? ""}
        placement="top"
      >
        <div onClick={() => callback(customerEmails, customerPhones)}>
          <ReactSVG
            src={ACTION_COMMUNICATION_ICON}
            className={classes.iconButton}
            color={ICON_BUTTON_COLOR}
          />
        </div>
      </Tooltip>
    );
  };

  const setPrintButton = (waybill: Waybill) => {
    const { statusShipment } = waybill;
    const isShipmentDeleted = statusShipment === ShipmentStatus.DELETED;

    if (isShipmentDeleted) return null;

    return (
      <Tooltip data-cy="action-print" title={translate("print") ?? ""} placement="top">
        <div>
          <FiPrinter color={ICON_BUTTON_COLOR} size={18} className={classes.iconButton} />
        </div>
      </Tooltip>
    );
  };

  const setExportToCsvButton = (loading?: boolean, callback?: () => void) => {
    return (
      <Tooltip title={translate("exportCSV")} placement="top">
        <div>
          <GenerateCSVButton
            loading={loading}
            customColor={Color.SECONDARY_DARK_OUTLINED}
            onClick={() => callback?.()}
          >
            {translate("exportCSV")}
          </GenerateCSVButton>
        </div>
      </Tooltip>
    );
  };

  const setAddNewShipment = () => {
    return (
      <Button
        style={{
          padding: "10px 20px 10px 16px",
        }}
        tooltip={translate("newShipment")}
        customColor={Color.SECONDARY_DARK}
        startIcon={<IconAdd />}
        onClick={() => history.push(PRIVATE_ROUTES.SHIPMENTS_MNT)}
      >
        {translate("newShipment")}
      </Button>
    );
  };

  const setSelectAllRecord = (title: string, callback?: () => void) => {
    return (
      // <Tooltip title={translate("selectAll")} placement="top">
      //   <div>
      //     <Button customColor={Color.TEXT_INFO} onClick={() => callback?.()}>
      //       <span
      //         style={{
      //           color: "#0055A4",
      //           fontSize: "12px",
      //           fontWeight: 600,
      //           lineHeight: "16px",
      //           fontStyle: "normal",
      //         }}
      //       >
      //         {title}
      //       </span>
      //     </Button>
      //   </div>
      // </Tooltip>

      <TextButton
        label={title}
        onClickHandler={() => {
          callback?.();
        }}
      />
    );
  };

  const setPreferences = (callback?: () => void) => {
    return (
      <IconButton
        onClick={() => {
          console.log(1111);
          callback?.();
        }}
      >
        <img src={SETTING_PREFERENCE} />
      </IconButton>
    );
  };

  return {
    setEditButton,
    setViewButton,
    setDeleteButton,
    setSendEmailButton,
    setPrintButton,
    setExportToCsvButton,
    setSelectAllRecord,
    setAddNewShipment,
    setPreferences,
  };
}
